import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Plus, Truck, Edit, Trash2, ArrowRight, ChevronLeft, ChevronRight, X } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const STATUS_MAP = {
  emesso:      { label: 'Emesso',      color: 'bg-blue-50 text-blue-700 border-blue-200' },
  in_transito: { label: 'In Transito', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  consegnato:  { label: 'Consegnato',  color: 'bg-green-50 text-green-700 border-green-200' },
  annullato:   { label: 'Annullato',   color: 'bg-red-50 text-red-700 border-red-200' },
};

const emptyForm = {
  date: new Date().toISOString().split('T')[0], type: 'in_entrata',
  supplier_id: '', site_id: '', carrier: '', reason: 'Vendita',
  external_ref: '', notes: '', lines: [],
};

export default function DDTPage() {
  const { isRole } = useAuth();
  const canManage = isRole('admin', 'qhse', 'capo_cantiere');
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [sites, setSites] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('__all__');
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus !== '__all__') params.status = filterStatus;
      const [dRes, sRes, siRes, invRes] = await Promise.all([
        api.get('/ddt', { params }),
        api.get('/suppliers/names'),
        api.get('/sites'),
        api.get('/inventory'),
      ]);
      setItems(dRes.data);
      setSuppliers(sRes.data);
      setSites(siRes.data);
      setInventoryItems(Array.isArray(invRes.data) ? invRes.data : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { load(); setPage(1); }, [load]);

  const openCreate = () => { setEditItem(null); setForm({ ...emptyForm, lines: [] }); setShowDialog(true); };
  const openEdit = (d) => {
    setEditItem(d);
    setForm({ date: d.date, type: d.type, supplier_id: d.supplier_id || '', site_id: d.site_id || '', carrier: d.carrier || '', reason: d.reason || '', external_ref: d.external_ref || '', notes: d.notes || '', lines: (d.lines || []).map(l => ({ ...l })) });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editItem) { await api.put(`/ddt/${editItem.id}`, form); }
      else { await api.post('/ddt', form); }
      setShowDialog(false); load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo DDT?')) return;
    try { await api.delete(`/ddt/${id}`); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleStatus = async (id, status) => {
    try { await api.post(`/ddt/${id}/status`, { status }); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { item_id: '', description: '', quantity: 1, unit: 'pz', notes: '' }] }));
  const updateLine = (idx, field, val) => setForm(f => { const l = [...f.lines]; l[idx] = { ...l[idx], [field]: val }; return { ...f, lines: l }; });
  const selectItem = (idx, itemId) => {
    const inv = inventoryItems.find(i => i.id === itemId);
    if (inv) {
      setForm(f => {
        const l = [...f.lines];
        l[idx] = { ...l[idx], item_id: itemId, description: inv.name, unit: inv.unit || 'pz' };
        return { ...f, lines: l };
      });
    }
  };
  const removeLine = (idx) => setForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

  const paged = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(items.length / PAGE_SIZE);

  return (
    <div className="space-y-6" data-testid="ddt-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">DDT - Documenti di Trasporto</h1>
          <p className="text-sm text-slate-500 mt-1">Gestione documenti di trasporto in entrata e uscita</p>
        </div>
        {canManage && <Button onClick={openCreate} data-testid="add-ddt-btn"><Plus size={16} className="mr-1" /> Nuovo DDT</Button>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(STATUS_MAP).map(([key, cfg]) => (
          <Card key={key} className="border-slate-200 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center"><Truck size={18} className="text-slate-600" /></div>
              <div><p className="text-xs text-slate-500">{cfg.label}</p><p className="text-xl font-bold text-slate-700">{items.filter(i => i.status === key).length}</p></div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Select value={filterStatus} onValueChange={setFilterStatus}>
        <SelectTrigger className="w-48" data-testid="ddt-status-filter"><SelectValue placeholder="Tutti gli stati" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">Tutti gli stati</SelectItem>
          {Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
        </SelectContent>
      </Select>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>N. DDT</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Fornitore/Dest.</TableHead>
                <TableHead>Cantiere</TableHead>
                <TableHead>Righe</TableHead>
                <TableHead>Stato</TableHead>
                {canManage && <TableHead className="w-32">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Caricamento...</TableCell></TableRow>
              ) : paged.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Nessun DDT</TableCell></TableRow>
              ) : paged.map(d => (
                <TableRow key={d.id} className="hover:bg-slate-50/50" data-testid={`ddt-row-${d.id}`}>
                  <TableCell className="font-mono text-sm font-medium">{d.ddt_number}</TableCell>
                  <TableCell className="text-sm">{d.date}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{d.type === 'in_entrata' ? 'Entrata' : 'Uscita'}</Badge></TableCell>
                  <TableCell className="text-sm max-w-[180px] truncate">{d.supplier_name || '-'}</TableCell>
                  <TableCell className="text-sm max-w-[150px] truncate">{d.site_name || '-'}</TableCell>
                  <TableCell className="text-sm text-center">{(d.lines || []).length}</TableCell>
                  <TableCell><Badge variant="outline" className={`text-[10px] ${STATUS_MAP[d.status]?.color || ''}`}>{STATUS_MAP[d.status]?.label || d.status}</Badge></TableCell>
                  {canManage && (
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(d)} data-testid={`edit-ddt-${d.id}`}><Edit size={13} /></Button>
                        {d.status === 'emesso' && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-amber-600" onClick={() => handleStatus(d.id, 'in_transito')} title="In Transito"><ArrowRight size={13} /></Button>}
                        {d.status === 'in_transito' && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-green-600" onClick={() => handleStatus(d.id, 'consegnato')} title="Consegnato"><ArrowRight size={13} /></Button>}
                        {isRole('admin') && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" onClick={() => handleDelete(d.id)} data-testid={`delete-ddt-${d.id}`}><Trash2 size={13} /></Button>}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-1">
          <p className="text-xs text-slate-500">{(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, items.length)} di {items.length}</p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={14} /></Button>
            <span className="text-xs text-slate-600 px-2">Pag. {page}/{totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={14} /></Button>
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader><DialogTitle>{editItem ? 'Modifica DDT' : 'Nuovo DDT'}</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-[65vh] pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Data *</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="ddt-date-input" /></div>
              <div className="space-y-1.5">
                <Label>Tipo</Label>
                <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="ddt-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_entrata">In Entrata</SelectItem>
                    <SelectItem value="in_uscita">In Uscita</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Fornitore/Destinatario</Label>
                <Select value={form.supplier_id} onValueChange={v => setForm({ ...form, supplier_id: v })}>
                  <SelectTrigger data-testid="ddt-supplier-select"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Cantiere</Label>
                <Select value={form.site_id} onValueChange={v => setForm({ ...form, site_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>{sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Vettore</Label><Input value={form.carrier} onChange={e => setForm({ ...form, carrier: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Causale</Label><Input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Rif. Esterno</Label><Input value={form.external_ref} onChange={e => setForm({ ...form, external_ref: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Note</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            </div>

            {/* Lines */}
            <div className="mt-4 border rounded-lg p-3 bg-slate-50/50">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Righe Merce</Label>
                <Button variant="outline" size="sm" onClick={addLine} data-testid="ddt-add-line"><Plus size={12} className="mr-1" /> Riga</Button>
              </div>
              {form.lines.length === 0 ? (
                <p className="text-xs text-slate-400 py-3 text-center">Nessuna riga</p>
              ) : form.lines.map((l, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-end">
                  <div className="col-span-3">
                    <Select value={l.item_id || 'manual'} onValueChange={v => { if (v === 'manual') { updateLine(idx, 'item_id', ''); } else { selectItem(idx, v); } }}>
                      <SelectTrigger className="text-xs h-9"><SelectValue placeholder="Articolo magazzino" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">-- Manuale --</SelectItem>
                        {inventoryItems.map(i => <SelectItem key={i.id} value={i.id}>{i.name} ({i.code || i.id.slice(0,6)})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3"><Input placeholder="Descrizione" value={l.description} onChange={e => updateLine(idx, 'description', e.target.value)} className="text-sm" /></div>
                  <div className="col-span-2"><Input type="number" placeholder="Qty" value={l.quantity} onChange={e => updateLine(idx, 'quantity', e.target.value)} className="text-sm" /></div>
                  <div className="col-span-1"><Input placeholder="U.M." value={l.unit} onChange={e => updateLine(idx, 'unit', e.target.value)} className="text-sm" /></div>
                  <div className="col-span-2"><Input placeholder="Note" value={l.notes || ''} onChange={e => updateLine(idx, 'notes', e.target.value)} className="text-sm" /></div>
                  <div className="col-span-1"><Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={() => removeLine(idx)}><X size={14} /></Button></div>
                </div>
              ))}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} data-testid="ddt-save-btn">{editItem ? 'Salva' : 'Crea DDT'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
